import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';

interface AudioPlayerContextType {
  play: (audio: HTMLAudioElement, key?: string) => Promise<void>;
  pause: () => void;
  stop: () => void;
  isPlaying: boolean;
  setRecording: (isRecording: boolean) => void;
  isRecording: boolean;
  currentAudioKey: string | null;
  currentTime: number;
  duration: number;
  formatTime: (seconds: number) => string;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  return ctx;
};

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentAudioKey, setCurrentAudioKey] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Format time helper function
  const formatTime = useCallback((seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Timer effect to update current time
  useEffect(() => {
    if (isPlaying && currentAudioRef.current) {
      timerRef.current = setInterval(() => {
        if (currentAudioRef.current) {
          setCurrentTime(currentAudioRef.current.currentTime);
        }
      }, 100); // Update every 100ms for smooth progress
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying]);

  // Audio event listeners
  useEffect(() => {
    const audio = currentAudioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      setIsPlaying(false);
      setCurrentAudioKey(null);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentAudioKey]);

  const play = useCallback(async (audio: HTMLAudioElement, key?: string) => {
    if (isRecording) {
      // Don't play audio if recording is active
      return;
    }
    if (currentAudioRef.current && currentAudioRef.current !== audio) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
    currentAudioRef.current = audio;
    try {
      await audio.play();
      setIsPlaying(true);
      setCurrentAudioKey(key || null);
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
    } catch (e) {
      setIsPlaying(false);
      setCurrentAudioKey(null);
      setCurrentTime(0);
    }
  }, [isRecording]);

  const pause = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      setIsPlaying(false);
      // Do NOT clear currentAudioKey here, so resume works
    }
  }, []);

  const stop = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentAudioKey(null);
      setCurrentTime(0);
    }
  }, []);

  const setRecordingState = useCallback((recording: boolean) => {
    setIsRecording(recording);
    if (recording && currentAudioRef.current) {
      currentAudioRef.current.pause();
      setIsPlaying(false);
      setCurrentAudioKey(null);
      setCurrentTime(0);
    }
  }, []);

  return (
    <AudioPlayerContext.Provider value={{ 
      play, 
      pause, 
      stop, 
      isPlaying, 
      setRecording: setRecordingState, 
      isRecording, 
      currentAudioKey,
      currentTime,
      duration,
      formatTime
    }}>
      {children}
    </AudioPlayerContext.Provider>
  );
}; 